import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { H1, H2, H3 } from '@piconetworks/pkg-heading'
import Picon from '@piconetworks/pkg-picon'
import { P } from '@piconetworks/pkg-text'
import * as Box from '@piconetworks/pkg-box'

import { useFeatureManager, useFeatureManagerMetrics } from 'features/FeatureManager'
import useUserId from 'hooks/useUserId'
import usePublisherId from 'hooks/usePublisherId'
import getAppStoreIcon from 'utils/icons/appStoreIcons'
import AccountModule from 'modules/account'

import BackButton from './BackButton'

import SubscriptionDetail from './SubscriptionDetail'
import IntegrationNotFound from './IntegrationNotFound'
import PlanSubscribe from './PlanSubscribe'
import PlanUpgrade from './PlanUpgrade'
import ExternalAppConnect from './ExternalIntegrationConnect'

import styles from './GenericPage.module.scss'

// a Store page with a CTA that when clicked triggers the Cart
const GenericPage = ({ appLabel }) => {
    const [isUpgradeVisible, setUpgradeVisibility] = useState(null)

    const showUpgrade = useCallback(() => {
        setUpgradeVisibility(true)
    }, [setUpgradeVisibility])

    const hideUpgrade = useCallback(() => {
        setUpgradeVisibility(false)
    }, [setUpgradeVisibility])

    const userId = useUserId()
    const publisherId = usePublisherId()
    const { getAppByLabel, getAppTierByLabel } = useFeatureManager({
        userId,
        publisherId,
    })
   
    const storeIcon = getAppStoreIcon(appLabel)
    const app = getAppByLabel(appLabel)
    const appNotFound = !app
    const tiers = app?.tiers

    const hasPhoneNumber = useSelector(AccountModule.selectors.hasPhoneNumber)
    const hasAccessToApp = tiers?.find(({ has_access }) => !!has_access)
    const hasFreeMessagingAccess = appLabel === 'messages' && !hasAccessToApp && hasPhoneNumber
    const appName = app?.name || ''
    const Usage = app?.Usage || null
    const PricingInfo = app?.PricingInfo || null

    const marketingDescription = app?.description || ''
    const featureList = app?.featureList || []
    const tierLabelGrantingAccess = tiers?.find(({ has_access }) => !!has_access)?.label
    const appTier = getAppTierByLabel(tierLabelGrantingAccess)
    const currentPricing = appTier?.pricings?.find((pricing) => {
            return !!pricing?.subscription && pricing?.subscription?.status !== 'canceled'
    })
    const pricingId = currentPricing?.id
    const isExternalApp = app.type === 'external'

    return (appNotFound) ? (
        <IntegrationNotFound />
    ) : (
        <>
            <div className={styles.genericPageContainer}>
                <div className={styles.backButton}>
                    <BackButton />
                </div>
                <div className={styles.IntegrationDetailsContainer}>
                    <div className={styles.overviewContainer}>
                        <div className={styles.iconContainer}>
                            <Picon icon="app-stripe" size="96" />
                        </div>
                        {appName && <H1 className={styles.title}>
                            {appName}
                        </H1>}
                        {marketingDescription && (
                            <P>{marketingDescription}</P>
                        )}
                        <div className={styles.divider} />
                        <div className={styles.pricing}>
                            <H3>Pricing</H3>
                            {PricingInfo ? <PricingInfo /> : 'Free'}
                        </div>
                    </div>
                    <div className={styles.infoContainer}>
                        {!hasAccessToApp && !hasFreeMessagingAccess && !isExternalApp && <PlanSubscribe appLabel={appLabel} />}
                        {isExternalApp && <ExternalAppConnect appLabel={appLabel} />}
                        {!isUpgradeVisible && (hasAccessToApp || hasFreeMessagingAccess) && <SubscriptionDetail appLabel={appLabel} showUpgrade={showUpgrade} />}
                        {isUpgradeVisible && <PlanUpgrade subscribedToPricingId={pricingId} appLabel={appLabel} hideUpgrade={hideUpgrade} />}

                        {(hasAccessToApp || isExternalApp) && Usage && (
                            <Usage />
                        )}
                    </div>
                </div>

                {featureList.length > 0 && (
                    <>
                        <H2>Features</H2>
                        <Box.Flexbox.Container
                            alignItems="start"
                            justifyContent="start"
                            flexDirection="column"
                            className="mt-2"
                        >
                            {featureList.map((feature, i) => {
                                return (
                                    <Box.Flexbox.Container
                                        key={i}
                                        alignItems="center"
                                        justifyContent="start"
                                        className="mb-3"
                                    >
                                        <Picon
                                            color="black"
                                            size="24"
                                            icon="icon_checkmark"
                                        />
                                        <P className={styles.feature}>
                                            {feature}
                                        </P>
                                    </Box.Flexbox.Container>
                                )
                            })}
                        </Box.Flexbox.Container>
                    </>
                )}
            </div>
        </>
    )
}
export default GenericPage
